import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PublicFooter } from '../../components/public/PublicFooter.js';
import { PublicNav } from '../../components/public/PublicNav.js';

const LAST_UPDATED = 'October 11, 2026';

export function TermsOfUsePage() {
  useEffect(() => {
    document.title = 'Terms — ScrubLedger';
  }, []);

  return (
    <div className="public-page">
      <PublicNav />
      <main className="public-simple-page public-simple-page--legal">
        <article className="public-legal">
          <h1>Terms of Use</h1>
          <p className="public-legal__updated">Last updated: {LAST_UPDATED}</p>

          <section>
            <h2>Acceptance of terms</h2>
            <p>
              These Terms of Use describe how ScrubLedger&apos;s public website and institution-provisioned
              workspaces may be used. Separate agreements between ScrubLedger and your institution may
              govern product access, data handling, and other terms. If you use ScrubLedger on behalf of an
              educational institution, use it only as authorized by that institution.
            </p>
          </section>

          <section>
            <h2>Description of ScrubLedger</h2>
            <p>
              ScrubLedger is a software service that helps Surgical Technology and similar programs manage
              clinical documentation workflows, structured case information, faculty review, requirement
              progress, and related program administration. Features available to you depend on your role,
              program configuration, and the agreement between ScrubLedger and your institution.
            </p>
          </section>

          <section>
            <h2>Authorized users</h2>
            <p>
              Access to authenticated workspaces is limited to users invited or provisioned by an
              institution or ScrubLedger platform administrators. You may not access programs, student
              records, or clinical files except as permitted by your membership and role.
            </p>
          </section>

          <section>
            <h2>Accounts and credentials</h2>
            <p>
              You are responsible for safeguarding your sign-in credentials and for activity under your
              account. Notify your program administrator and ScrubLedger support promptly if you suspect
              unauthorized access. We may suspend or terminate accounts that violate these terms or pose a
              security risk.
            </p>
          </section>

          <section>
            <h2>Institution and program responsibilities</h2>
            <p>
              Institutions are responsible for determining who receives access, configuring program
              requirements, supervising clinical documentation practices, and complying with their own
              policies and applicable laws. ScrubLedger provides tools; institutions remain responsible for
              academic, clinical, and records decisions made using those tools.
            </p>
          </section>

          <section>
            <h2>Acceptable use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the service for unlawful, harmful, or fraudulent purposes</li>
              <li>Attempt to bypass authentication, authorization, or rate limits</li>
              <li>Probe, scan, or test vulnerabilities except with our prior written permission</li>
              <li>Upload malware or interfere with the integrity or performance of the service</li>
              <li>Access or exfiltrate data belonging to another tenant or user</li>
            </ul>
          </section>

          <section>
            <h2>Clinical and academic records</h2>
            <p>
              Content submitted through ScrubLedger may include educational and clinical documentation
              subject to institutional policies. How that content is stored, processed, and accessed in the
              product is described in our Privacy Policy and in agreements between ScrubLedger and your
              institution. Institutions and users are responsible for submitting content in line with their
              policies and applicable law.
            </p>
          </section>

          <section>
            <h2>Data and content submitted</h2>
            <p>
              You retain ownership of content you submit. ScrubLedger may generate derived data (such as
              extracted fields, processing metadata, and audit entries) as part of normal operation.
              Aggregated or de-identified operational metrics may be used to maintain and improve the
              service without identifying individuals.
            </p>
          </section>

          <section>
            <h2>Intellectual property</h2>
            <p>
              ScrubLedger, including its software, branding, documentation, and visual design, is protected
              by intellectual property laws. These terms do not grant you ownership of the service or its
              underlying technology. Product access is provided through your institution&apos;s provisioning
              and any applicable agreement with ScrubLedger.
            </p>
          </section>

          <section>
            <h2>Availability and changes</h2>
            <p>
              We strive to keep ScrubLedger available and may update features, interfaces, or infrastructure
              from time to time. We may perform maintenance that temporarily affects availability. We do
              not guarantee uninterrupted or error-free operation.
            </p>
          </section>

          <section>
            <h2>Suspension and termination</h2>
            <p>
              We may suspend or terminate access for violations of these terms, security concerns, non-payment
              under a separate agreement, or as required by law. Institutions may request deactivation of
              users or programs through authorized administrators. Upon termination, access to workspaces may
              end; retention of stored data is governed by institutional agreements and our Privacy Policy.
            </p>
          </section>

          <section>
            <h2>Disclaimers</h2>
            <p>
              SCRUBLEDGER IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS TO THE
              MAXIMUM EXTENT PERMITTED BY LAW. WE DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR
              STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
              AND NON-INFRINGEMENT. SCRUBLEDGER DOES NOT PROVIDE MEDICAL, LEGAL, OR REGULATORY ADVICE, AND
              OUTPUTS SUCH AS EXTRACTED CASE FIELDS REQUIRE HUMAN REVIEW BY QUALIFIED PROGRAM STAFF.
            </p>
          </section>

          <section>
            <h2>Limitation of liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SCRUBLEDGER AND ITS SUPPLIERS WILL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
              DATA, GOODWILL, OR BUSINESS INTERRUPTION, ARISING FROM OR RELATED TO YOUR USE OF THE SERVICE,
              EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. ANY CAP ON DIRECT DAMAGES, AND ANY OTHER
              LIABILITY TERMS, ARE GOVERNED BY APPLICABLE LAW AND ANY SEPARATE WRITTEN AGREEMENT BETWEEN
              SCRUBLEDGER AND YOUR INSTITUTION, WHERE SUCH AN AGREEMENT EXISTS.
            </p>
          </section>

          <section>
            <h2>Changes to these terms</h2>
            <p>
              We may modify these Terms of Use by posting an updated version on this page. Material changes
              will be indicated by updating the &quot;Last updated&quot; date. Review this page periodically
              for updates.
            </p>
          </section>

          <section>
            <h2>Contact</h2>
            <p>
              Questions about these Terms of Use:{' '}
              <a href="mailto:support@scrubledger.com">support@scrubledger.com</a>.
            </p>
          </section>

          <p className="public-legal__back">
            <Link to="/" className="btn btn--ghost">Back to home</Link>
          </p>
        </article>
      </main>
      <PublicFooter />
    </div>
  );
}
